import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../../hooks/useUsers";
import { useTeamBoardsList, useTeamBoard } from "../../../hooks/useTeamBoards";
import { buildBoardAssigneePickerOptions } from "../utils/teamBoardCardFilters";

const CreateLinkedTaskDialog = ({
  open,
  onClose,
  sourceBoard,
  onSubmit,
  isSubmitting = false,
  /** Prefill responsáveis (ex.: atalho da busca geral). */
  initialAssignees = [],
  dialogTitle,
  dialogIntro,
  submitLabel,
  boardHelperText,
  assigneesHelperText,
}) => {
  const { t } = useTranslation("teamTasks");
  const { data: usersData } = useUsers();
  const users = usersData?.users || usersData || [];
  const wasOpenRef = useRef(false);
  const lastTargetBoardIdRef = useRef(null);
  const initialAssigneesRef = useRef(initialAssignees);

  const { data: workspaceBoards = [], isLoading: boardsLoading } = useTeamBoardsList({
    workspace: 1,
    enabled: open,
  });

  const [title, setTitle] = useState("");
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [assignees, setAssignees] = useState([]);

  const boardOptions = useMemo(() => {
    const list = Array.isArray(workspaceBoards) ? [...workspaceBoards] : [];
    if (sourceBoard?.id && !list.some((b) => Number(b.id) === Number(sourceBoard.id))) {
      list.unshift(sourceBoard);
    }
    return list;
  }, [workspaceBoards, sourceBoard]);

  const targetBoardId = selectedBoard?.id ?? sourceBoard?.id;
  const { data: targetBoardDetail, isLoading: targetBoardLoading } = useTeamBoard(targetBoardId, {
    enabled: open && Boolean(targetBoardId),
  });

  // Só colunas do painel destino. Nunca reutilizar colunas do painel de origem
  // enquanto o detalhe do destino ainda carrega (ids diferentes → 400 na API).
  const columnOptions = useMemo(() => {
    const targetId = targetBoardId != null ? Number(targetBoardId) : null;
    if (!targetId) return [];

    const detailId =
      targetBoardDetail?.id != null ? Number(targetBoardDetail.id) : null;
    if (detailId === targetId && Array.isArray(targetBoardDetail?.columns)) {
      return targetBoardDetail.columns.filter((c) => !c.isDone);
    }

    if (
      sourceBoard?.id != null &&
      Number(sourceBoard.id) === targetId &&
      Array.isArray(sourceBoard.columns)
    ) {
      return sourceBoard.columns.filter((c) => !c.isDone);
    }

    return [];
  }, [targetBoardDetail, targetBoardId, sourceBoard]);

  // Membros do painel destino; se o painel não tiver lista, cai na lista geral do workspace.
  const assigneeOptions = useMemo(
    () =>
      buildBoardAssigneePickerOptions(
        targetBoardDetail?.members || [],
        Array.isArray(users) ? users : []
      ),
    [targetBoardDetail?.members, users]
  );

  const seedAssignees = () => {
    const seed = Array.isArray(initialAssigneesRef.current)
      ? initialAssigneesRef.current
      : [];
    return seed
      .map((u) => ({
        id: Number(u?.id),
        nome: u?.nome || u?.name || String(u?.id || ""),
      }))
      .filter((u) => Number.isFinite(u.id) && u.id > 0);
  };

  // Só reinicia o formulário ao abrir o dialog — não quando `sourceBoard` muda de referência.
  useEffect(() => {
    initialAssigneesRef.current = initialAssignees;
    if (open && !wasOpenRef.current) {
      setTitle("");
      setAssignees(seedAssignees());
      setSelectedBoard(sourceBoard || null);
      setSelectedColumn(null);
      lastTargetBoardIdRef.current = null;
    }
    wasOpenRef.current = open;
  }, [open, sourceBoard, initialAssignees]);

  // Sem painel de origem (atalho da busca): seleciona o primeiro painel disponível.
  useEffect(() => {
    if (!open || sourceBoard || selectedBoard || !boardOptions.length) return;
    setSelectedBoard(boardOptions[0]);
  }, [open, sourceBoard, selectedBoard, boardOptions]);

  // Atualiza coluna padrão e re-aplica responsáveis ao trocar o painel destino.
  // Também revalida se a coluna selecionada ainda pertence às opções do painel atual.
  useEffect(() => {
    if (!open) return;
    const nextId = targetBoardId != null ? Number(targetBoardId) : null;
    const boardChanged = lastTargetBoardIdRef.current !== nextId;
    if (boardChanged) {
      lastTargetBoardIdRef.current = nextId;
      setSelectedColumn(columnOptions[0] || null);
      setAssignees(seedAssignees());
      return;
    }
    const selectedStillValid =
      selectedColumn != null &&
      columnOptions.some((c) => Number(c.id) === Number(selectedColumn.id));
    if (!selectedStillValid) {
      setSelectedColumn(columnOptions[0] || null);
    }
  }, [open, targetBoardId, columnOptions, selectedColumn]);

  // Se os membros do painel carregarem depois, remove assignees que não pertencem mais.
  // Quando a lista ainda está vazia (carregando), preserva o seed inicial.
  useEffect(() => {
    if (!open || !assignees.length || !assigneeOptions.length) return;
    const allowed = new Set(assigneeOptions.map((u) => Number(u.id)));
    setAssignees((prev) => {
      const next = prev
        .map((u) => {
          const id = Number(u.id);
          const match = assigneeOptions.find((o) => Number(o.id) === id);
          return match || (allowed.has(id) ? u : null);
        })
        .filter(Boolean);
      return next.length === prev.length &&
        next.every((u, i) => Number(u.id) === Number(prev[i].id))
        ? prev
        : next;
    });
  }, [open, assigneeOptions, assignees.length]);

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || isSubmitting) return;
    const ok = await onSubmit?.({
      title: trimmed,
      boardId: selectedBoard?.id != null ? Number(selectedBoard.id) : undefined,
      columnId: selectedColumn?.id != null ? Number(selectedColumn.id) : undefined,
      assignees: assignees.map((u) => Number(u.id)),
    });
    if (ok !== false) onClose?.();
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle || t("linkedTaskCreateTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dialogIntro || t("linkedTaskCreateIntro")}
        </Typography>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            label={t("cardTitle")}
            placeholder={t("cardTitlePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Autocomplete
            options={boardOptions}
            loading={boardsLoading}
            value={selectedBoard}
            onChange={(_, value) => setSelectedBoard(value)}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            disabled={isSubmitting}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("linkedTaskBoardLabel")}
                helperText={boardHelperText || t("linkedTaskBoardHint")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {boardsLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            options={columnOptions}
            value={selectedColumn}
            onChange={(_, value) => setSelectedColumn(value)}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            loading={targetBoardLoading && !columnOptions.length}
            disabled={isSubmitting || !columnOptions.length}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("linkedTaskColumnLabel")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {targetBoardLoading && !columnOptions.length ? (
                        <CircularProgress size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            multiple
            options={assigneeOptions}
            value={assignees}
            onChange={(_, value) => setAssignees(value)}
            getOptionLabel={(opt) => opt?.nome || opt?.name || String(opt?.id || "")}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            disabled={isSubmitting || !assigneeOptions.length}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("cardAssignees")}
                placeholder={t("cardAssigneesPlaceholder")}
                helperText={assigneesHelperText || t("linkedTaskAssigneesHint")}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!title.trim() || isSubmitting || !selectedColumn}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitLabel || t("linkedTaskCreate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLinkedTaskDialog;
